import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import Stats from 'three/examples/jsm/libs/stats.module';
import apiClient from '../../../../services/api';
import store from '../../../../store';
import { setStoreData } from '../../../../store/actions/Store';
import { useTheme } from '@emotion/react';

import MapRightSidebar from './MapRightSidebar';
import { useThree } from '@react-three/fiber';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

import { LoadingScreen } from '../../../partials/LoadingScreen';

const StorePreview = () => {
    const mountRef = useRef(null);
    const frameIdRef = useRef(null);
    const moveForward = useRef(null);
    const moveBackward = useRef(null);
    const moveLeft = useRef(null);
    const moveRight = useRef(null);
    const cameraRef = useRef(null);

    const { floorPlanId } = useParams();

    const [data, setData] = useState(null);
    const [fixtures, setFixtures] = useState([]);
    const [loadingText, setLoadingText] = useState('');
    const [loadingValue, setLoadingValue] = useState('');
    const [loading, setLoading] = useState(false);

    const storeData = useSelector((state) => state.store.storeData);
    const sideBarIsOpened = useSelector((state) => state.store.sideBarIsOpened);

    const theme = useTheme();

    useEffect(() => {
        if (storeData) {
            console.log(storeData);
            setData(storeData);

            storeData.fixtures?.forEach(fixture => {
                setFixtures((prevFixtures) => [...prevFixtures, fixture.external_id]);
            });
        } else {
            apiClient.post('/api/easy-floor/get', { id: floorPlanId }).then(response => {
                response.data.floor_plan.width = response.data.floor_plan.width * response.data.floor_plan.scale;
                response.data.floor_plan.depth = response.data.floor_plan.depth * response.data.floor_plan.scale;
                response.data.floor_plan.width = response.data.texture.width * response.data.floor_plan.scale / 10;
                response.data.floor_plan.depth = response.data.texture.height * response.data.floor_plan.scale / 10;

                store.dispatch(setStoreData(response.data));

                response.data.fixtures.forEach(fixture => {
                    setFixtures((prevFixtures) => [...prevFixtures, fixture.external_id]);
                });
            });
        }
    }, [storeData]);

    useEffect(() => {
        if(!data) {
            return;
        }

        let scene, camera, renderer, controls, stats;
        let products = [];

        // Initialize Three.js Scene
        function init() {
            const width = mountRef.current.clientWidth;
            const height = mountRef.current.clientHeight;

            // Create scene
            scene = new THREE.Scene();
            scene.background = new THREE.Color(theme.palette.background.default);

            // Create camera
            camera = new THREE.PerspectiveCamera(45, width / height, 1, 50000);

            // Set camera position based on floor plan dimensions
            if (data) {
                camera.position.set(
                    data.floor_plan.width / 2,
                    Math.hypot(data.floor_plan.width, data.floor_plan.depth),
                    data.floor_plan.depth
                );
            }

            // Create renderer
            renderer = new THREE.WebGLRenderer({ antialias: true });
            renderer.setSize(width, height);
            mountRef.current.appendChild(renderer.domElement);

            // Add orbit controls
            controls = new OrbitControls(camera, renderer.domElement);

            //controls.listenToKeyEvents(window);
            controls.target.set(
                data.floor_plan.width / 2,
                0,
                data.floor_plan.depth / 2
            );
            controls.maxDistance = 50000;

            // Add lights
            const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
            scene.add(ambientLight);

            const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
            directionalLight.position.set(0, 2, 3);
            scene.add(directionalLight);

            // Add stats (optional)
            stats = Stats();
            //mountRef.current.appendChild(stats.dom);

            window.addEventListener('resize', handleResize);
            window.addEventListener('keydown', handleKeyDown);
            window.addEventListener('keyup', handleKeyUp);

            // Load store data and initialize scene
            setUpData(data);

            // Start animation loop
            animate();
        }

        const handleResize = (camera) => {
            if(!camera) {
                return;
            }

            camera.aspect = window.innerWidth / window.innerHeight;

            camera.updateProjectionMatrix();

            renderer.setSize(window.innerWidth, window.innerHeight);
        }

        if(!sideBarIsOpened) {
            handleResize(camera);
        }

        const handleKeyDown = (event) => {
            switch (event.code) {
                case 'KeyW': // Move forward
                    moveForward.current = true;
                    break;
                case 'KeyS': // Move backward
                    moveBackward.current = true;
                    break;
                case 'KeyA': // Move left
                    moveLeft.current = true;
                    break;
                case 'KeyD': // Move right
                    moveRight.current = true;
                    break;
                default:
                    break;
            }
        }

        const handleKeyUp = (event) => {
            switch (event.code) {
                case 'KeyW':
                    moveForward.current = false;
                    break;
                case 'KeyS':
                    moveBackward.current = false;
                    break;
                case 'KeyA':
                    moveLeft.current = false;
                    break;
                case 'KeyD':
                    moveRight.current = false;
                    break;
                default:
                    break;
            }
        }

        function setUpData(data) {
            console.log('aaaaaaa', data);
            setFixtures(data.fixtures.map((fixture) => fixture.external_id));
            createFloor();
            createGrid();
            createWalls();
            createProjects(data.floor_plan_elements_3D);
        }

        function createGrid()
        {
            const size = data.floor_plan.width * 5;
            const divisions = 300;
            const gridHelper = new THREE.GridHelper( size, divisions, theme.palette.border.grid, theme.palette.border.grid );

            gridHelper.position.set(
                Math.round((Math.abs(data.floor_plan.width) / 2)),
                0,
                Math.round((Math.abs(data.floor_plan.depth) / 2))
            );

            scene.add(gridHelper);
        }

        function createFloor() {
            const floorGeometry = new THREE.BoxGeometry(
                data.floor_plan.width,
                0,
                data.floor_plan.depth
            );
            const floorMaterial = new THREE.MeshLambertMaterial({
                color: 0x000000,
                transparent: true,
                opacity: 0.2,
            });
            const floor = new THREE.Mesh(floorGeometry, floorMaterial);
            floor.position.set(data.floor_plan.width / 2, 0, data.floor_plan.depth / 2);
            scene.add(floor);
        }

        function createWalls() {
            data.floor_plan_elements.forEach((element) => {
                if (element.is_wall) {
                    createElement(element);
                }
            });
        }

        function createElement(element) {
            const position = getElementPosition(element);
            const size = getElementSize(element);

            const geometry = new THREE.BoxGeometry(...size);
            const material = new THREE.MeshLambertMaterial({
                color: element.is_wall === 0 ? element.hex : 0x000000,
            });

            const mesh = new THREE.Mesh(geometry, material);
            mesh.position.set(...position);
            mesh.rotation.y = (Number(element.angle) * Math.PI) / 180;
            scene.add(mesh);
        }

        function getElementPosition(element) {
            const height = element.is_wall === 0 ? element.height : 200;

            let positionX = element.positionX || element.position?.positionX;
            let positionY = element.positionY || element.position?.positionY;

            if(element.is_wall) {
                positionX = element.positionX;
                positionY = element.positionY;
            }

            return [
                Number(positionX) + Math.round(Math.abs(element.width) / 2),
                height / 2,
                Number(positionY) + Math.round(Math.abs(element.depth) / 2),
            ];
        }

        function getElementSize(element) {
            const height = element.is_wall === 0 ? element.height : 200;
            return [element.width, height, element.depth];
        }

        function createProjectGroup(models, project) {
            const group = new THREE.Group();
            const projectFixtures = new THREE.Group();
            const projectProducts = new THREE.Group();

            models.forEach((model, index) => {
                let isProduct = false;

                if(index > 0) {
                    //return;
                }
                
                const instanceProductGroups = new Map();
                const instanceFixtureGroups = new Map();

                model.scene.traverse((object) => {
                    const { userData } = object;
        
                    if (object.isMesh) {
                        if (fixtures?.includes(Number(userData.EBMeshId))) {
                            const key = `${object.geometry.uuid}_${object.material.uuid}`;

                            object.updateWorldMatrix(true, false);

                            if (!instanceFixtureGroups.has(key)) {
                                instanceFixtureGroups.set(key, { geometry: object.geometry, material: object.material, transforms: [] });
                            }

                            const transform = new THREE.Matrix4().copy(object.matrixWorld);
                            instanceFixtureGroups.get(key).transforms.push(transform);
                        } else {
                            const key = `${object.geometry.uuid}_${object.material.uuid}`;

                            object.updateWorldMatrix(true, false);

                            if (!instanceProductGroups.has(key)) {
                                instanceProductGroups.set(key, { geometry: object.geometry, material: object.material, transforms: [] });
                            }

                            const transform = new THREE.Matrix4().copy(object.matrixWorld);
                            instanceProductGroups.get(key).transforms.push(transform);
                        }
                    }

                    if(object.isMesh === true && object.material.map !== null) {
                        object.material.map.anisotropy = renderer.capabilities.getMaxAnisotropy();
                    }
                });

                for (const { geometry, material, transforms } of instanceFixtureGroups.values()) {
                    const instancedMesh = new THREE.InstancedMesh(geometry, material, transforms.length);
                
                    // Apply each stored transform to an instance
                    transforms.forEach((matrix, index) => {
                        instancedMesh.setMatrixAt(index, matrix);
                    });
                
                    // Add the instanced mesh to the scene
                    projectFixtures.add(instancedMesh);
                }

                for (const { geometry, material, transforms } of instanceProductGroups.values()) {
                    const instancedMesh = new THREE.InstancedMesh(geometry, material, transforms.length);
                
                    // Apply each stored transform to an instance
                    transforms.forEach((matrix, index) => {
                        instancedMesh.setMatrixAt(index, matrix);
                    });
                
                    // Add the instanced mesh to the scene
                    projectProducts.add(instancedMesh);
                }
            });

            projectFixtures.visible = true;
            projectProducts.visible = true;
            projectProducts.userData = {'isProduct': true};

            group.add(projectFixtures);
            group.add(projectProducts);

            group.userData = {'projectID': project.id};

            return group;
        }

        async function createProjects(projects) {
            setLoading(true);

            for (const project of projects) {
                if (project.models && project.models?.length != 0) {
                    setLoadingText(project.name);
                    setLoadingValue(0);
                    const models = await loadProjectModelsByChunk(project.models, 10);
                    const group = createProjectGroup(models, project);
        
                    project.positions.forEach(position => {
                        group.position.set(
                            position.positionX,
                            position.positionZ,
                            position.positionY
                        );
        
                        if (position.angle) {
                            const angleInRadians = Number(360 - position.angle) * (Math.PI / 180);
                            group.rotation.y = angleInRadians;
        
                            const bbox = new THREE.Box3().setFromObject(group);
        
                            group.position.set(
                                Number(position.positionX) + Math.abs(Number(position.positionX) - bbox.min.x),
                                Number(position.positionZ),
                                Number(position.positionY) + Math.abs(Number(position.positionY) - bbox.min.z)
                            );
                        }
        
                        scene.add(group.clone());
                    });
                } else {
                    await loadProjectModel(project);
                }
            }

            setLoading(false);
        }
        
        function loadProjectModelsByChunk(models, chunkSize) {
            const loader = new GLTFLoader();
        
            async function loadInChunks(models) {
                const result = [];
                for (let i = 0; i < models.length; i += chunkSize) {
                    setLoadingValue((i * 100) / models.length);
                    const chunk = models.slice(i, i + chunkSize);
                    const loadedChunk = await Promise.all(
                        chunk.map((url) =>
                            loader.loadAsync(url).then((gltf) => ({
                                url,
                                scene: gltf.scene,
                            }))
                        )
                    );
                    result.push(...loadedChunk);
                }
        
                return result;
            }
        
            return loadInChunks(models);
        }

        function loadProjectModels(models) {
            const loader = new GLTFLoader();

            return Promise.all(
                models.map((url) =>
                    loader.loadAsync(url).then((gltf) => ({
                        url,
                        scene: gltf.scene,
                    }))
                )
            );
        }

        function loadProjectModel(project) {
            const loader = new GLTFLoader();
            const model = project.model;
            const positions = project.positions;

            if(!model?.gltf) {
                return;
            }

            loader.load(
                (model.gltf?.optimized ? model.gltf?.optimized : model.gltf?.default),
                function(gltf) {
                    const group = createProjectGroup([gltf], project, true);
        
                    positions.forEach(position => {
                        group.position.set(
                            position.positionX,
                            position.positionZ,
                            position.positionY
                        );
        
                        if (position.angle) {
                            const angleInRadians = Number(360 - position.angle) * (Math.PI / 180);
                            group.rotation.y = angleInRadians;
        
                            const bbox = new THREE.Box3().setFromObject(group);
        
                            group.position.set(
                                Number(position.positionX) + Math.abs(Number(position.positionX) - bbox.min.x),
                                Number(position.positionZ),
                                Number(position.positionY) + Math.abs(Number(position.positionY) - bbox.min.z)
                            );
                        }
        
                        scene.add(group.clone());
                    });
                }
            )
        }

        function check(camera) {
            const distanceThreshold = 1500;

            scene.children.forEach((object) => {
                if(!object.isGroup) {
                    return;
                }

                object.children.forEach((group) => {
                    if (!group.userData?.isProduct) {
                        return;
                    }

                    const distance = camera.position.distanceTo(object.position);
                    group.visible = distance < distanceThreshold;
                });
            });
        }

        function animate() {
            if (moveForward.current) {
                camera.translateZ(-10);
            }

            if (moveBackward.current) {
                camera.translateZ(10);
            }

            if (moveLeft.current) {
                camera.translateX(-10);
            }

            if (moveRight.current) {
                camera.a(10);
            }

            frameIdRef.current = requestAnimationFrame(animate);

            controls.update();
            renderer.render(scene, camera);
            stats.update();

            //check(camera);
        }

        init();

        // Clean up on unmount
        return () => {
            // Stop the animation loop
            if (frameIdRef.current) {
                cancelAnimationFrame(frameIdRef.current);
            }

            // Dispose of scene objects
            scene.traverse((object) => {
                if (object.geometry) object.geometry.dispose();
                if (object.material) {
                    if (object.material.map) object.material.map.dispose();
                    object.material.dispose();
                }
            });

            // Dispose the renderer and its DOM element
            renderer.dispose();

            if (renderer.domElement) {
                mountRef.current?.removeChild(renderer.domElement);
            }
        };
    }, [data, sideBarIsOpened]);

    return (
        <>
            <LoadingScreen
                text={"Loading "+loadingText}
                display={loading}
                value={loadingValue}
                noProgress={false}
                variant='determinate'
                top='10%'
                left='50%'
            />
            <div ref={mountRef} style={{ height: '100vh', width: '100%' }} />
            <div style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', zIndex: 9999 }}>
                <MapRightSidebar />
            </div>
        </>
    );
};

export default StorePreview;
